import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  CheckCircle,
  Clock,
  Lock,
  Play,
  BookOpen,
  Video,
  FileText,
  Monitor,
  DollarSign,
  Star,
  ArrowRight,
  Target,
  Trophy,
  Zap,
  Brain,
  Code,
  Award,
  TrendingUp,
  RefreshCw,
  AlertCircle,
  ExternalLink
} from 'lucide-react';

import Card from '../components/ui/Card';
import ProgressBar from '../components/ui/ProgressBar';
import Loading from '../components/common/Loading';
import WeeklyPlanView from '../components/planner/WeeklyPlanView';
import { roadmapAPI } from '../services/api';
import toast from 'react-hot-toast';

const RoadmapPage = () => {
  const [state, setState] = useState({
    roadmaps: [],
    selectedRoadmap: null,
    selectedModule: null,
    isLoading: true,
    isUpdating: false,
    error: null,
    filter: 'all'
  });

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 300], [0, -100]);
  const timelineY = useTransform(scrollY, [0, 500], [0, -50]);
  
  const navigate = useNavigate();
  const location = useLocation();

  const fetchRoadmaps = useCallback(async (preferredRoadmapId = null, showLoader = true) => {
    try {
      if (showLoader) {
        setState(prev => ({ ...prev, isLoading: true, error: null }));
      } else {
        setState(prev => ({ ...prev, error: null }));
      }
      console.log('Fetching user roadmaps...');

      const roadmapsData = await roadmapAPI.getRoadmaps();

      if (roadmapsData && roadmapsData.length > 0) {
        const targetRoadmap = roadmapsData.find(r => r.id === preferredRoadmapId) || roadmapsData[0];
        const detailedRoadmap = await roadmapAPI.getRoadmapById(targetRoadmap.id);

        const currentModule = detailedRoadmap.modules?.find(m => m.status === 'current') ||
          detailedRoadmap.modules?.find(m => m.status === 'in_progress') ||
          detailedRoadmap.modules?.[0];

        setState(prev => {
          const selectedModule = detailedRoadmap.modules?.find(m => m.id === prev.selectedModule?.id) || currentModule;
          return {
            ...prev,
            roadmaps: roadmapsData,
            selectedRoadmap: detailedRoadmap,
            selectedModule,
            isLoading: false
          };
        });
      } else {
        setState(prev => ({
          ...prev,
          error: 'No roadmaps found. Please complete onboarding first.',
          isLoading: false
        }));
      }
    } catch (error) {
      console.error('Failed to fetch roadmaps:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to load your learning roadmap. Please try again.',
        isLoading: false
      }));
    }
  }, []);

  // Fetch roadmaps
  useEffect(() => {
    fetchRoadmaps();
  }, [fetchRoadmaps, location.search]);

  useEffect(() => {
    const refreshOnFocus = () => {
      if (!document.hidden && state.selectedRoadmap?.id) {
        fetchRoadmaps(state.selectedRoadmap.id, false);
      }
    };

    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnFocus);

    return () => {
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnFocus);
    };
  }, [fetchRoadmaps, state.selectedRoadmap?.id]);

  // Mark module complete
  const handleModuleComplete = async (moduleId) => {
    try {
      setState(prev => ({ ...prev, isUpdating: true }));
      await roadmapAPI.markModuleComplete(state.selectedRoadmap.id, moduleId);
      await fetchRoadmaps(state.selectedRoadmap.id, false);
      setState(prev => ({ ...prev, isUpdating: false }));
      
      toast.success('Module completed! 🚀');
    } catch (error) {
      console.error('❌ Failed to complete module:', error);
      toast.error(error?.message || 'Failed to complete module');
      setState(prev => ({ ...prev, isUpdating: false }));
    }
  };

  // Loading state
  if (state.isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loading />
          <p className="text-white mt-4 text-lg">Loading your personalized roadmap...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (state.error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <AlertCircle className="w-24 h-24 text-red-400 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-white mb-4">Roadmap Not Available</h2>
          <p className="text-gray-400 mb-8">{state.error}</p>
          <Link to="/onboarding">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-lg inline-flex items-center"
            >
              <Target className="w-5 h-5 mr-2" />
              Complete Setup
            </motion.button>
          </Link>
        </div>
      </div>
    );
  }

  if (!state.selectedRoadmap) {
    return <Loading />;
  }

  // Helper functions
  const getModuleStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-green-400" />;
      case 'current':
      case 'in_progress':
        return <Play className="w-6 h-6 text-purple-400" />;
      case 'locked':
        return <Lock className="w-6 h-6 text-gray-500" />;
      default:
        return <Clock className="w-6 h-6 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'from-green-500 to-emerald-500';
      case 'current':
      case 'in_progress':
        return 'from-purple-500 to-pink-500';
      case 'locked':
        return 'from-gray-500 to-gray-600';
      default:
        return 'from-blue-500 to-cyan-500';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/20';
      case 'current':
      case 'in_progress':
        return 'bg-purple-500/20';
      case 'locked':
        return 'bg-gray-500/20';
      default:
        return 'bg-blue-500/20';
    }
  };

  // ✅ Get resource icon
  const getResourceIcon = (type) => {
    switch (type) {
      case 'video':
        return <Video className="w-5 h-5 text-red-400" />;
      case 'free_material':
      case 'documentation':
        return <FileText className="w-5 h-5 text-blue-400" />;
      case 'practice':
        return <Code className="w-5 h-5 text-green-400" />;
      case 'paid_course':
      case 'course':
        return <DollarSign className="w-5 h-5 text-purple-400" />;
      default:
        return <BookOpen className="w-5 h-5 text-gray-400" />;
    }
  };

  // Filter modules
  const filteredModules = state.selectedRoadmap.modules?.filter(module => {
    if (state.filter === 'all') return true;
    if (state.filter === 'completed') return module.status === 'completed';
    if (state.filter === 'current') return module.status === 'current' || module.status === 'in_progress';
    if (state.filter === 'locked') return module.status === 'locked';
    return true;
  }) || [];

  // Roadmap stats
  const roadmapStats = {
    totalModules: state.selectedRoadmap.modules?.length || 0,
    completedModules: state.selectedRoadmap.modules?.filter(m => m.status === 'completed').length || 0,
    inProgressModules: state.selectedRoadmap.modules?.filter(m => m.status === 'current' || m.status === 'in_progress').length || 0,
    overallProgress: state.selectedRoadmap.progress || 0
  };
  const hasInsights =
    (state.selectedRoadmap.skill_gap_analysis && state.selectedRoadmap.skill_gap_analysis.length > 0) ||
    (state.selectedRoadmap.recommended_tools && state.selectedRoadmap.recommended_tools.length > 0) ||
    (state.selectedRoadmap.career_tips && state.selectedRoadmap.career_tips.length > 0);

  // Floating element
  const FloatingElement = ({ children, className, delay = 0 }) => (
    <motion.div
      className={`absolute ${className}`}
      animate={{
        y: [-20, 20, -20],
        rotate: [-5, 5, -5],
        opacity: [0.1, 0.3, 0.1]
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: "easeInOut",
        delay: delay
      }}
    >
      {children}
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden pt-16">
      {/* Floating Background */}
      <div className="absolute inset-0 opacity-10">
        <FloatingElement className="top-20 left-10 text-purple-400" delay={0}>
          <Target size={60} />
        </FloatingElement>
        <FloatingElement className="top-40 right-20 text-blue-400" delay={2}>
          <Brain size={50} />
        </FloatingElement>
        <FloatingElement className="bottom-40 left-20 text-pink-400" delay={4}>
          <Trophy size={70} />
        </FloatingElement>
        <FloatingElement className="bottom-20 right-10 text-green-400" delay={6}>
          <Zap size={55} />
        </FloatingElement>
      </div>

      {/* Hero Section */}
      <motion.section 
        className="relative z-10 py-10 md:py-12 px-4 sm:px-6"
        style={{ y: heroY }}
      >
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              Your Learning
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"> Roadmap</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
              🎯 {state.selectedRoadmap.career_goal || state.selectedRoadmap.careerGoal} • ⏱️ {state.selectedRoadmap.estimated_duration || state.selectedRoadmap.estimatedDuration} • 📊 {roadmapStats.overallProgress}% Complete
            </p>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 max-w-5xl mx-auto"
          >
            {[
              { label: 'Total Modules', value: roadmapStats.totalModules, icon: BookOpen, color: 'from-blue-500 to-cyan-500' },
              { label: 'Completed', value: roadmapStats.completedModules, icon: CheckCircle, color: 'from-green-500 to-emerald-500' },
              { label: 'In Progress', value: roadmapStats.inProgressModules, icon: Play, color: 'from-purple-500 to-pink-500' },
              { label: 'Progress', value: `${roadmapStats.overallProgress}%`, icon: TrendingUp, color: 'from-orange-500 to-red-500' }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="p-5 md:p-6 bg-slate-800/30 backdrop-blur-xl rounded-2xl border border-slate-700/50 relative overflow-hidden"
              >
                <div className="flex items-center justify-between mb-4">
                  <motion.div
                    className={`w-12 h-12 bg-gradient-to-r ${stat.color} rounded-xl flex items-center justify-center`}
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.5 }}
                  >
                    <stat.icon className="w-6 h-6 text-white" />
                  </motion.div>
                </div>
                <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>

          {/* Overall Progress Bar */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-6 max-w-3xl mx-auto"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400">Overall Progress</span>
              <span className="text-purple-400 font-bold">{roadmapStats.overallProgress}%</span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-4 overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${roadmapStats.overallProgress}%` }}
                transition={{ duration: 2, ease: "easeOut" }}
              />
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* AI-Powered Insights */}
      {hasInsights && (
        <motion.section 
          className="relative z-10 px-4 sm:px-6 mb-6 md:mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="max-w-7xl mx-auto">
            <Card className="p-5 md:p-6 bg-slate-800/30 backdrop-blur-xl border-slate-700/50">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                <Brain className="w-6 h-6 text-purple-400 mr-2" />
                AI-Powered Learning Insights
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Skills to Develop */}
                {state.selectedRoadmap.skill_gap_analysis && state.selectedRoadmap.skill_gap_analysis.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-purple-400 mb-3 flex items-center">
                      <TrendingUp className="w-4 h-4 mr-1" />
                      Skills to Develop
                    </h3>
                    <div className="space-y-2">
                      {state.selectedRoadmap.skill_gap_analysis.slice(0, 4).map((skill, index) => (
                        <div key={index} className="flex items-start text-sm text-gray-300">
                          <div className="w-2 h-2 bg-purple-400 rounded-full mr-2 mt-1.5 flex-shrink-0"></div>
                          <span className="line-clamp-2">{skill}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommended Tools */}
                {state.selectedRoadmap.recommended_tools && state.selectedRoadmap.recommended_tools.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-blue-400 mb-3 flex items-center">
                      <Monitor className="w-4 h-4 mr-1" />
                      Recommended Tools
                    </h3>
                    <div className="space-y-2">
                      {state.selectedRoadmap.recommended_tools.slice(0, 4).map((tool, index) => (
                        <div key={index} className="flex items-start text-sm text-gray-300">
                          <div className="w-2 h-2 bg-blue-400 rounded-full mr-2 mt-1.5 flex-shrink-0"></div>
                          <span className="line-clamp-1">{typeof tool === 'string' ? tool : tool.name || tool}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Career Tips */}
                {state.selectedRoadmap.career_tips && state.selectedRoadmap.career_tips.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-green-400 mb-3 flex items-center">
                      <Award className="w-4 h-4 mr-1" />
                      Career Tips
                    </h3>
                    <div className="space-y-2">
                      {state.selectedRoadmap.career_tips.slice(0, 3).map((tip, index) => (
                        <div key={index} className="flex items-start text-sm text-gray-300">
                          <div className="w-2 h-2 bg-green-400 rounded-full mr-2 mt-1.5 flex-shrink-0"></div>
                          <span className="line-clamp-2">{tip.substring(0, 100)}{tip.length > 100 ? '...' : ''}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </motion.section>
      )}

      <section className="relative z-10 px-4 sm:px-6 mb-6 md:mb-8">
        <div className="max-w-7xl mx-auto">
          <WeeklyPlanView />
        </div>
      </section>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pb-12 md:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 xl:grid-cols-3 gap-6 xl:gap-8 items-start">
          
          {/* Modules Timeline */}
          <motion.div 
            className="lg:col-span-3 xl:col-span-2"
            style={{ y: timelineY }}
          >
            {/* Filter Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-wrap gap-3 mb-6"
            >
              {[
                { key: 'all', label: 'All Modules' },
                { key: 'completed', label: 'Completed' },
                { key: 'current', label: 'In Progress' },
                { key: 'locked', label: 'Locked' }
              ].map((filter) => (
                <motion.button
                  key={filter.key}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setState(prev => ({ ...prev, filter: filter.key }))}
                  className={`px-5 py-2.5 rounded-xl font-medium transition-all duration-300 ${
                    state.filter === filter.key
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                      : 'bg-slate-800/50 text-gray-400 hover:bg-slate-700/50 hover:text-white'
                  }`}
                >
                  {filter.label}
                </motion.button>
              ))}
            </motion.div>

            {/* Modules Grid */}
            <div className="space-y-5">
              {filteredModules.length > 0 ? filteredModules.map((module, index) => (
                <motion.div
                  key={module.id}
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02, x: 10 }}
                  className={`p-5 md:p-6 bg-slate-800/30 backdrop-blur-xl rounded-2xl border border-slate-700/50 cursor-pointer transition-all duration-300 ${getStatusBg(module.status)} relative overflow-hidden ${
                    state.selectedModule?.id === module.id ? 'ring-2 ring-purple-500' : ''
                  }`}
                  onClick={() => setState(prev => ({ ...prev, selectedModule: module }))}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-3">
                        <motion.div
                          whileHover={{ scale: 1.1 }}
                          className="mr-3"
                        >
                          {getModuleStatusIcon(module.status)}
                        </motion.div>
                        <h3 className="text-xl font-bold text-white">
                          {index + 1}. {module.title}
                        </h3>
                        <span className={`ml-3 px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${getStatusColor(module.status)} text-white`}>
                          {(module.status || 'upcoming').toUpperCase().replace('_', ' ')}
                        </span>
                      </div>
                      
                      <p className="text-gray-400 mb-4 line-clamp-2">{module.description}</p>
                      
                      <div className="flex items-center space-x-6 text-sm">
                        <div className="flex items-center text-gray-400">
                          <Clock className="w-4 h-4 mr-1" />
                          {module.estimated_hours || module.estimatedHours || 0}h
                        </div>
                        <div className="flex items-center text-gray-400">
                          <BookOpen className="w-4 h-4 mr-1" />
                          {module.resources?.length || 0} resources
                        </div>
                        {module.difficulty && (
                          <div className="flex items-center text-gray-400">
                            <Star className="w-4 h-4 mr-1" />
                            {module.difficulty}
                          </div>
                        )}
                      </div>

                      {/* Progress Bar */}
                      {(module.status === 'current' || module.status === 'in_progress') && (
                        <div className="mt-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-gray-400 text-sm">Progress</span>
                            <span className="text-purple-400 font-medium text-sm">{module.progress || 0}%</span>
                          </div>
                          <ProgressBar value={module.progress || 0} size="small" />
                        </div>
                      )}
                    </div>

                    <motion.div
                      whileHover={{ x: 5 }}
                      className="ml-4"
                    >
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                    </motion.div>
                  </div>

                  {/* Action Buttons */}
                  {module.status !== 'locked' && (
                    <div className="flex items-center space-x-3 mt-6">
                      <Link to={`/lesson/${module.id}?roadmapId=${state.selectedRoadmap.id}`}>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium"
                        >
                          <Play className="w-4 h-4 mr-2" />
                          {module.status === 'completed' ? 'Review' : 'Start Learning'}
                        </motion.button>
                      </Link>
                      
                      {module.assessment && (
                        <Link to={`/assessment/${module.id}?roadmapId=${state.selectedRoadmap.id}`}>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center px-4 py-2 bg-slate-700 hover:bg-slate-600 text-gray-300 rounded-lg"
                          >
                            <Award className="w-4 h-4 mr-2" />
                            Assessment
                          </motion.button>
                        </Link>
                      )}

                      {(module.status === 'current' || module.status === 'in_progress') && module.progress >= 80 && (
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleModuleComplete(module.id);
                          }}
                          disabled={state.isUpdating}
                          className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg"
                        >
                          {state.isUpdating ? (
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mr-2" />
                          )}
                          Complete
                        </motion.button>
                      )}
                    </div>
                  )}

                  <motion.div
                    className="absolute -top-10 -right-10 w-32 h-32 bg-purple-500/5 rounded-full blur-xl"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.05, 0.15, 0.05]
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      delay: index * 0.5
                    }}
                  />
                </motion.div>
              )) : (
                <div className="text-center py-12">
                  <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No modules found</h3>
                  <p className="text-gray-400">Try changing the filter to see different modules.</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Module Details Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="lg:col-span-2 xl:col-span-1 lg:sticky lg:top-24 self-start"
          >
            {state.selectedModule ? (
              <div className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-5 md:p-6 border border-slate-700/50">
                <div className="flex items-center mb-4">
                  {getModuleStatusIcon(state.selectedModule.status)}
                  <h3 className="text-xl font-bold text-white ml-3">
                    {state.selectedModule.title}
                  </h3>
                </div>

                <p className="text-gray-400 mb-6">{state.selectedModule.description}</p>

                {/* Module Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-slate-700/30 rounded-xl">
                    <div className="text-2xl font-bold text-white">
                      {state.selectedModule.completed_hours || state.selectedModule.completedHours || 0}
                    </div>
                    <div className="text-gray-400 text-sm">Hours Completed</div>
                  </div>
                  <div className="p-4 bg-slate-700/30 rounded-xl">
                    <div className="text-2xl font-bold text-white">
                      {state.selectedModule.estimated_hours || state.selectedModule.estimatedHours || 0}
                    </div>
                    <div className="text-gray-400 text-sm">Total Hours</div>
                  </div>
                </div>

                {/* Progress */}
                {(state.selectedModule.status === 'current' || state.selectedModule.status === 'in_progress') && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-400">Progress</span>
                      <span className="text-purple-400 font-bold">{state.selectedModule.progress || 0}%</span>
                    </div>
                    <ProgressBar value={state.selectedModule.progress || 0} />
                  </div>
                )}

                {/* Skills */}
                {state.selectedModule.skills && state.selectedModule.skills.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-400 mb-3">Skills You'll Learn</h4>
                    <div className="flex flex-wrap gap-2">
                      {state.selectedModule.skills.map((skill, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-purple-500/20 text-purple-300 text-xs rounded-full border border-purple-500/30"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* ✅ ENHANCED RESOURCES SECTION */}
                {state.selectedModule.resources && state.selectedModule.resources.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-semibold text-white flex items-center">
                        <BookOpen className="w-5 h-5 text-purple-400 mr-2" />
                        Learning Resources
                      </h4>
                      <span className="text-xs text-gray-400">
                        {state.selectedModule.resources.length} available
                      </span>
                    </div>
                    
                    <div className="space-y-3">
                      {state.selectedModule.resources.map((resource, index) => (
                        <motion.div
                          key={resource.id || index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ x: 5, scale: 1.02 }}
                          onClick={() => {
                            console.log('🔗 Opening resource:', resource);
                            
                            if (resource.url) {
                              window.open(resource.url, '_blank', 'noopener,noreferrer');
                              toast.success(`Opening: ${resource.title}`, {
                                icon: '🚀',
                                duration: 2000
                              });
                            } else {
                              toast.error('Resource link not available');
                            }
                          }}
                          className="group flex items-start p-3 bg-slate-700/30 hover:bg-slate-700/50 rounded-xl border border-slate-600/30 hover:border-purple-500/50 transition-all cursor-pointer"
                        >
                          {/* Icon */}
                          <div className="flex-shrink-0 mr-3 mt-0.5">
                            {getResourceIcon(resource.type)}
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            {/* Title & Arrow */}
                            <div className="flex items-start justify-between">
                              <h5 className="text-sm font-semibold text-white group-hover:text-purple-300 transition-colors line-clamp-1 pr-2">
                                {resource.title}
                              </h5>
                              <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-purple-400 transition-all flex-shrink-0" />
                            </div>
                            
                            {/* Description */}
                            {resource.description && (
                              <p className="text-xs text-gray-400 mt-1 line-clamp-2">
                                {resource.description}
                              </p>
                            )}
                            
                            {/* Provider & Duration */}
                            <div className="flex items-center mt-2 text-xs text-gray-500">
                              <span className="truncate">{resource.provider}</span>
                              <span className="mx-2">•</span>
                              <span>{resource.duration}</span>
                            </div>
                            
                            {/* Why Recommended */}
                            {resource.why_recommended && (
                              <div className="mt-2 p-2 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                                <p className="text-xs text-purple-300 line-clamp-2">
                                  <span className="font-semibold">💡 Why:</span> {resource.why_recommended}
                                </p>
                              </div>
                            )}
                            
                            {/* Topics Covered */}
                            {resource.topics_covered && resource.topics_covered.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {resource.topics_covered.slice(0, 3).map((topic, idx) => (
                                  <span 
                                    key={idx}
                                    className="px-2 py-0.5 bg-slate-600/50 text-gray-300 text-xs rounded-full"
                                  >
                                    {topic}
                                  </span>
                                ))}
                              </div>
                            )}
                            
                            {/* Rating & Cost */}
                            <div className="flex items-center justify-between mt-3">
                              {resource.rating && (
                                <div className="flex items-center">
                                  <Star className="w-3.5 h-3.5 text-yellow-400 fill-current mr-1" />
                                  <span className="text-xs text-gray-400 font-medium">{resource.rating}</span>
                                </div>
                              )}
                              
                              <div className="flex items-center gap-2">
                                {/* Difficulty */}
                                {resource.difficulty && (
                                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                                    resource.difficulty === 'beginner' ? 'bg-green-500/20 text-green-300' :
                                    resource.difficulty === 'intermediate' ? 'bg-yellow-500/20 text-yellow-300' :
                                    'bg-red-500/20 text-red-300'
                                  }`}>
                                    {resource.difficulty}
                                  </span>
                                )}
                                
                                {/* Cost */}
                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                  resource.cost === 'free' 
                                    ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                                    : 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                                }`}>
                                  {resource.cost === 'free' ? '✓ Free' : '💎 Paid'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ✅ ENHANCED ASSESSMENT SECTION */}
                {state.selectedModule.assessment && (
                  <div className="border-t border-slate-700 pt-4">
                    <h4 className="text-lg font-semibold text-white mb-3">Assessment</h4>
                    <div className="p-4 bg-slate-700/30 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="text-sm font-medium text-white">
                          {state.selectedModule.assessment.title || 'Module Assessment'}
                        </h5>
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded-full">
                          {state.selectedModule.assessment.questions?.length || 5} Questions
                        </span>
                      </div>
                      
                      <div className="text-xs text-gray-400 space-y-1 mb-4">
                        <p>⏱️ Duration: {state.selectedModule.assessment.duration_minutes || state.selectedModule.assessment.durationMinutes || 20} minutes</p>
                        <p>✅ Pass Score: {state.selectedModule.assessment.pass_score || state.selectedModule.assessment.passScore || 70}%</p>
                      </div>
                      
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => navigate(`/assessment/${state.selectedModule.id}?roadmapId=${state.selectedRoadmap.id}`)}
                        className="w-full flex items-center justify-center px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all"
                      >
                        <Award className="w-4 h-4 mr-2" />
                        {state.selectedModule.status === 'completed' ? 'Review Assessment' : 'Take Assessment'}
                      </motion.button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 text-center">
                <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Select a Module</h3>
                <p className="text-gray-400 text-sm">
                  Click on any module to view its details and resources
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RoadmapPage;

